import { useContext } from 'react';

import { AccessContext, type AccessContextValue } from '@/contexts/AccessContext';
import type { Module } from '@/lib/modules';

/** The access of the signed-in session. Throws outside an `AccessProvider`. */
export function useAccess(): AccessContextValue {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used within AccessProvider');
  }
  return context;
}

/**
 * useModuleGate returns true when the session may issue a read that the server
 * admits for any module of `modules`. Hooks AND it into their `enabled` flag so
 * an employee without the module never gets a 403 on page load.
 *
 * The gate is UX only; the server is the boundary. Outside an `AccessProvider`
 * the gate is OPEN, so components rendered in isolation (most unit tests) keep
 * their pre-#1670 behaviour. The app shell always mounts the provider around the
 * route tree, so production always gates.
 */
export function useModuleGate(modules: readonly Module[]): boolean {
  const context = useContext(AccessContext);
  if (!context) return true;
  return context.isAdmin || modules.some(context.hasModule);
}
